import {
  pgTable,
  pgEnum,
  serial,
  integer,
  text,
  timestamp,
  date,
  boolean,
  varchar,
  jsonb,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { carriers } from "./business";
import { plans } from "./plans";

export const availabilityScopeEnum = pgEnum("availability_scope", [
  "statewide",
  "rating_area",
  "county",
  "zip",
]);

/**
 * GeoRatingAreaMap - Simplified ZIP to Rating Area lookup
 * 
 * A fast lookup table for resolving ZIP codes to rating areas.
 * This is the primary table used by the quoting engine for geo resolution.
 */
export const geoRatingAreaMap = pgTable(
  "geo_rating_area_map",
  {
    id: serial("id").primaryKey(),
    state: varchar("state", { length: 2 }).notNull(),
    // Null for county-based rows (match_type = 'county'); a 5-digit ZIP for
    // 'zip' rows or a 3-digit prefix for 'zip_prefix' rows.
    zip5: varchar("zip5", { length: 5 }),
    county: varchar("county", { length: 100 }),
    // How this row is matched: 'zip' (exact zip5) | 'zip_prefix' (3-digit) | 'county'.
    matchType: varchar("match_type", { length: 20 }).notNull().default("zip"),
    ratingAreaId: varchar("rating_area_id", { length: 20 }).notNull(),
    ratingAreaName: varchar("rating_area_name", { length: 100 }),
    source: varchar("source", { length: 100 }).notNull().default("manual"),
    effectiveStart: date("effective_start"),
    effectiveEnd: date("effective_end"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    stateZipIdx: index("geo_rating_area_map_state_zip_idx").on(t.state, t.zip5),
    zipIdx: index("geo_rating_area_map_zip_idx").on(t.zip5),
    ratingAreaIdx: index("geo_rating_area_map_rating_area_idx").on(t.ratingAreaId),
    stateCountyIdx: index("geo_rating_area_map_state_county_idx").on(t.state, t.county),
    stateMatchTypeIdx: index("geo_rating_area_map_state_matchtype_idx").on(t.state, t.matchType),
  })
);

/**
 * CarrierEligibilityRule - Rules for carrier eligibility
 * 
 * Defines which carriers are available based on SIC codes, group size, etc.
 */
export const carrierEligibilityRules = pgTable(
  "carrier_eligibility_rules",
  {
    id: serial("id").primaryKey(),
    carrierId: integer("carrier_id")
      .notNull()
      .references(() => carriers.id, { onDelete: "restrict" }),
    state: varchar("state", { length: 2 }).notNull(),
    sicAllowed: text("sic_allowed").array(),
    sicExcluded: text("sic_excluded").array(),
    minGroupSize: integer("min_group_size"),
    maxGroupSize: integer("max_group_size"),
    countyInclude: text("county_include").array(),
    countyExclude: text("county_exclude").array(),
    zipInclude: text("zip_include").array(),
    zipExclude: text("zip_exclude").array(),
    notes: text("notes"),
    effectiveStart: date("effective_start"),
    effectiveEnd: date("effective_end"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    carrierStateIdx: index("carrier_eligibility_rules_carrier_state_idx").on(t.carrierId, t.state),
    stateIdx: index("carrier_eligibility_rules_state_idx").on(t.state),
    stateFormat: check(
      "carrier_eligibility_rules_state_format",
      sql`${t.state} ~ '^[A-Z]{2}$'`
    ),
  })
);

/**
 * Availability Scope enum
 */
export const AvailabilityScope = {
  STATEWIDE: "statewide",
  RATING_AREA: "rating_area",
  COUNTY: "county",
  ZIP: "zip",
} as const;

export type AvailabilityScopeType = typeof AvailabilityScope[keyof typeof AvailabilityScope];

/**
 * PlanAvailabilityRule - Rules for plan service area availability
 * 
 * Defines where specific plans are available geographically.
 */
export const planAvailabilityRules = pgTable(
  "plan_availability_rules",
  {
    id: serial("id").primaryKey(),
    planId: integer("plan_id")
      .notNull()
      .references(() => plans.id, { onDelete: "cascade" }),
    carrierId: integer("carrier_id")
      .notNull()
      .references(() => carriers.id, { onDelete: "restrict" }),
    state: varchar("state", { length: 2 }).notNull(),
    availabilityScope: availabilityScopeEnum("availability_scope")
      .notNull()
      .default("statewide"),
    allowedRatingAreas: integer("allowed_rating_areas").array(),
    allowedCounties: text("allowed_counties").array(),
    allowedZips: text("allowed_zips").array(),
    excludedRatingAreas: integer("excluded_rating_areas").array(),
    excludedCounties: text("excluded_counties").array(),
    excludedZips: text("excluded_zips").array(),
    effectiveStart: date("effective_start"),
    effectiveEnd: date("effective_end"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (t) => ({
    planStateIdx: index("plan_availability_rules_plan_state_idx").on(t.planId, t.state),
    carrierStateIdx: index("plan_availability_rules_carrier_state_idx").on(t.carrierId, t.state),
    stateFormat: check(
      "plan_availability_rules_state_format",
      sql`${t.state} ~ '^[A-Z]{2}$'`
    ),
  })
);

/**
 * QuoteValidationAuditLog - Audit log for quote validation
 * 
 * Records all validation decisions made during quoting for compliance and debugging.
 */
export const quoteValidationAuditLogs = pgTable(
  "quote_validation_audit_logs",
  {
    id: serial("id").primaryKey(),
    quoteId: integer("quote_id"),
    clientId: integer("client_id"),
    prospectId: integer("prospect_id"),
    validationType: varchar("validation_type", { length: 50 }).notNull(),
    inputPayload: jsonb("input_payload"),
    geoContext: jsonb("geo_context"),
    errors: jsonb("errors"),
    warnings: jsonb("warnings"),
    eligiblePlans: jsonb("eligible_plans"),
    excludedPlans: jsonb("excluded_plans"),
    createdBy: varchar("created_by", { length: 100 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    quoteIdx: index("quote_validation_audit_logs_quote_idx").on(t.quoteId),
    clientIdx: index("quote_validation_audit_logs_client_idx").on(t.clientId),
    prospectIdx: index("quote_validation_audit_logs_prospect_idx").on(t.prospectId),
  })
);

// Insert schemas
export const insertGeoRatingAreaMapSchema = createInsertSchema(geoRatingAreaMap).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCarrierEligibilityRuleSchema = createInsertSchema(carrierEligibilityRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPlanAvailabilityRuleSchema = createInsertSchema(planAvailabilityRules).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertQuoteValidationAuditLogSchema = createInsertSchema(quoteValidationAuditLogs).omit({
  id: true,
  createdAt: true,
});

// Types
export type GeoRatingAreaMap = typeof geoRatingAreaMap.$inferSelect;
export type InsertGeoRatingAreaMap = z.infer<typeof insertGeoRatingAreaMapSchema>;
export type CarrierEligibilityRule = typeof carrierEligibilityRules.$inferSelect;
export type InsertCarrierEligibilityRule = z.infer<typeof insertCarrierEligibilityRuleSchema>;
export type PlanAvailabilityRule = typeof planAvailabilityRules.$inferSelect;
export type InsertPlanAvailabilityRule = z.infer<typeof insertPlanAvailabilityRuleSchema>;
export type QuoteValidationAuditLog = typeof quoteValidationAuditLogs.$inferSelect;
export type InsertQuoteValidationAuditLog = z.infer<typeof insertQuoteValidationAuditLogSchema>;

/**
 * Validation Reason Codes
 * 
 * Machine-readable codes for validation decisions.
 */
export const ValidationReasonCode = {
  // ZIP/Geo validation
  ZIP_INVALID_FORMAT: "ZIP_INVALID_FORMAT",
  ZIP_NOT_FOUND: "ZIP_NOT_FOUND",
  RATING_AREA_NOT_FOUND: "RATING_AREA_NOT_FOUND",
  
  // SIC/Industry validation
  SIC_INVALID_FORMAT: "SIC_INVALID_FORMAT",
  NAICS_INVALID_FORMAT: "NAICS_INVALID_FORMAT",
  INDUSTRY_CODE_MISSING: "INDUSTRY_CODE_MISSING",
  SIC_INELIGIBLE_FOR_CARRIER: "SIC_INELIGIBLE_FOR_CARRIER",
  
  // Plan/Carrier eligibility
  PLAN_NOT_OFFERED_IN_STATE: "PLAN_NOT_OFFERED_IN_STATE",
  PLAN_INACTIVE_FOR_EFFECTIVE_DATE: "PLAN_INACTIVE_FOR_EFFECTIVE_DATE",
  CARRIER_GROUP_SIZE_INELIGIBLE: "CARRIER_GROUP_SIZE_INELIGIBLE",
  CARRIER_NOT_AVAILABLE_IN_STATE: "CARRIER_NOT_AVAILABLE_IN_STATE",
  
  // Service area validation
  EMPLOYEE_ZIP_OUTSIDE_SERVICE_AREA: "EMPLOYEE_ZIP_OUTSIDE_SERVICE_AREA",
  EMPLOYEE_RATING_AREA_OUTSIDE_SERVICE_AREA: "EMPLOYEE_RATING_AREA_OUTSIDE_SERVICE_AREA",
  EMPLOYEE_COUNTY_OUTSIDE_SERVICE_AREA: "EMPLOYEE_COUNTY_OUTSIDE_SERVICE_AREA",
  GROUP_ZIP_OUTSIDE_SERVICE_AREA: "GROUP_ZIP_OUTSIDE_SERVICE_AREA",
  ZIP_TO_RATING_AREA_UNRESOLVED: "ZIP_TO_RATING_AREA_UNRESOLVED",
  
  // Quote input validation
  GROUP_STATE_MISSING: "GROUP_STATE_MISSING",
  EFFECTIVE_DATE_MISSING: "EFFECTIVE_DATE_MISSING",
  EFFECTIVE_DATE_INVALID: "EFFECTIVE_DATE_INVALID",
  GROUP_ZIP_INVALID: "GROUP_ZIP_INVALID",
  EMPLOYEE_COUNT_ZERO: "EMPLOYEE_COUNT_ZERO",
  EMPLOYEE_ZIP_INVALID: "EMPLOYEE_ZIP_INVALID",
  
  // API constraints
  API_CONSTRAINT_EXCLUSION: "API_CONSTRAINT_EXCLUSION",
} as const;

export type ValidationReasonCodeType = typeof ValidationReasonCode[keyof typeof ValidationReasonCode];

/**
 * Resolution Status for geo context
 */
export const ResolutionStatus = {
  RESOLVED: "resolved",
  PARTIAL: "partial",
  UNRESOLVED: "unresolved",
} as const;

export type ResolutionStatusType = typeof ResolutionStatus[keyof typeof ResolutionStatus];

/**
 * Validation Error/Warning interface
 */
export interface ValidationMessage {
  code: ValidationReasonCodeType;
  message: string;
  field?: string;
  details?: Record<string, unknown>;
}

/**
 * Geo Context interface
 */
export interface GeoContext {
  state: string;
  zip5: string;
  county?: string;
  ratingAreaId?: string;
  ratingAreaSource?: string;
  resolutionStatus: ResolutionStatusType;
}

/**
 * Quote Validation Result interface
 */
export interface QuoteValidationResult {
  isValid: boolean;
  errors: ValidationMessage[];
  warnings: ValidationMessage[];
  normalizedPayload: Record<string, unknown>;
  geoContext: {
    group: GeoContext;
    employees: GeoContext[];
  };
}

/**
 * Plan Exclusion Result interface
 */
export interface PlanExclusionResult {
  planId: number;
  planName?: string;
  carrierId?: number;
  carrierName?: string;
  reasonCode: ValidationReasonCodeType;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Plan Eligibility Filter Result interface
 */
export interface PlanEligibilityResult {
  eligiblePlans: Array<{ planId: number; planName?: string }>;
  excludedPlans: PlanExclusionResult[];
}
